/**
 * Helpers to keep numeric filter inputs in the same units the user sees.
 *
 * When a column uses a `divisor` number format (e.g. cents → dollars via
 * divisor 100), the report displays `raw / divisor` (5250 → "$52.50").
 * Filter values, however, are sent to OpenSearch in the raw stored units.
 * To avoid the confusing mismatch where a user types "52.50" expecting
 * dollars but filters against cents, we convert at the input boundary:
 *
 *   - display → storage: multiply by the divisor (user types 52.50 → store 5250)
 *   - storage → display: divide by the divisor (stored 5250 → show 52.50)
 *
 * Storage stays in raw units, so query building, persistence, exports, and
 * previously-saved reports are all unaffected.
 */

import type { ColumnFormatMap } from './column-format-options';

/**
 * Returns the active divisor for a field's column format, or undefined when
 * there is no divisor-formatted column for that field (no transform needed).
 */
export function getColumnDivisor(
  formatMap: ColumnFormatMap | undefined,
  fieldKey: string
): number | undefined {
  const fmt = formatMap?.[fieldKey];
  if (fmt?.type === 'number' && fmt.divisor && fmt.divisor !== 0) {
    return fmt.divisor;
  }
  return undefined;
}

/** Round away binary floating-point dust while preserving real precision. */
function clean(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * Convert a raw stored number to the display value the user expects.
 * Returns the input unchanged when there is no divisor.
 */
export function rawToDisplayNumber(raw: number, divisor: number | undefined): number {
  if (!divisor) return raw;
  return clean(raw / divisor);
}

/**
 * Convert a user-entered display value back to raw stored units.
 * Returns the input unchanged when there is no divisor.
 */
export function displayToRawNumber(displayValue: number, divisor: number | undefined): number {
  if (!divisor) return displayValue;
  return clean(displayValue * divisor);
}
